<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  CalendarDays,
  Check,
  CircleDotDashed,
  Filter,
  Flag,
  GanttChartSquare,
  KanbanSquare,
  LayoutList,
  ListChecks,
  Plus,
  Search,
  Settings as SettingsIcon,
  Trash2,
  X,
} from "lucide-vue-next";
import AppShell from "@/components/workspace/AppShell.vue";
import ActivityPanel from "@/components/views/ActivityPanel.vue";
import CalendarBoard from "@/components/views/CalendarBoard.vue";
import FilesPanel from "@/components/views/FilesPanel.vue";
import GanttBoard from "@/components/views/GanttBoard.vue";
import KanbanBoard from "@/components/views/KanbanBoard.vue";
import OverviewPanel from "@/components/views/OverviewPanel.vue";
import PlanList from "@/components/views/PlanList.vue";
import RisksPanel from "@/components/views/RisksPanel.vue";
import SettingsPanel from "@/components/views/SettingsPanel.vue";
import TaskDetail from "@/components/task/TaskDetail.vue";
import TaskDeleteDialog from "@/components/task/TaskDeleteDialog.vue";
import PersonAvatar from "@/components/people/PersonAvatar.vue";
import { useWorkspaceStore } from "@/stores/workspace";
import { ApiError } from "@/services/api";
import {
  addProjectWorkingDays,
  calculateProjectFinish,
  fullDate,
  isProjectWorkingDay,
  shortDate,
  todayLocalDate,
} from "@/utils/dates";
import { calculateTaskProgress } from "@/utils/progress";
import { calculateScheduleHealth } from "@/utils/schedule-health";
import {
  DEFAULT_VISIBLE_STATUSES,
  filterPlanTasks,
  hasCustomStatusFilter as customStatusFilterIsActive,
  planTaskOrganizationOptions,
} from "@/utils/task-filters";
import type {
  BulkTaskEditChanges,
  BulkTaskEditPreview,
  TaskStatus,
  Visibility,
} from "@/types/models";
import {
  taskDeletionBlockers,
  taskDeletionBlockersFromDetails,
  type TaskDeletionBlocker,
} from "@/utils/task-deletion";
import { requiresBulkScheduleReview } from "@/utils/bulk-edit";

const workspace = useWorkspaceStore();
const route = useRoute();
const router = useRouter();
type NewItemKind =
  "task" | "checklist" | "questionnaire" | "summary" | "milestone" | "section";

const quickAdd = ref(false);
const newKind = ref<NewItemKind>("task");
const newTitle = ref("");
const newSection = ref("");
const newVisibility = ref<Visibility>("Shared");
const newStartDate = ref("");
const newDuration = ref(5);
const quickInput = ref<HTMLInputElement | null>(null);
const quickDialog = ref<HTMLElement | null>(null);
const filterOpen = ref(false);
const pendingTaskStatus = ref<TaskStatus>();
const expandedPlanSectionIds = ref<string[]>([]);
const attentionExpansionPending = ref(false);
const bulkMode = ref(false);
const selectedTaskIds = ref<string[]>([]);
const BULK_SELECTION_LIMIT = 50;
const bulkOwner = ref("");
const bulkSection = ref("");
const bulkStartDate = ref("");
const bulkStatus = ref<TaskStatus | "" | "__delete__">("");
const bulkPreview = ref<BulkTaskEditPreview | null>(null);
const bulkChecking = ref(false);
const bulkApplying = ref(false);
const bulkError = ref("");
const bulkDeleteOpen = ref(false);
const bulkDeleteError = ref("");
const bulkDeleting = ref(false);
const bulkDeleteServerBlockers = ref<TaskDeletionBlocker[]>([]);
let quickAddReturnFocus: HTMLElement | null = null;
let priorBodyOverflow = "";

const projectId = computed(() => String(route.params.projectId));
const view = computed(() => String(route.params.view || "list"));
const project = computed(() => workspace.active?.project);
const headerParticipants = computed(() =>
  (workspace.active?.participants || []).slice(0, 4),
);
const remainingHeaderParticipants = computed(() =>
  (workspace.active?.participants || []).slice(4),
);
const newItemNeedsSchedule = computed(() =>
  ["task", "checklist", "questionnaire", "milestone"].includes(newKind.value),
);
const newItemUsesDuration = computed(() =>
  ["task", "checklist", "questionnaire"].includes(newKind.value),
);
const newItemLabel = computed(() => {
  const labels: Record<NewItemKind, string> = {
    task: "Task",
    checklist: "Checklist",
    questionnaire: "Questionnaire",
    summary: "Task collection",
    milestone: "Milestone",
    section: "Section",
  };
  return labels[newKind.value];
});
const newCalculatedFinish = computed(() => {
  if (!newItemUsesDuration.value || !newStartDate.value) return "";
  try {
    return calculateProjectFinish(
      newStartDate.value,
      Number(newDuration.value),
      workspace.active?.project.workingWeekdays || [1, 2, 3, 4, 5],
      workspace.active?.project.holidays || [],
    );
  } catch {
    return "";
  }
});
const newScheduleError = computed(() => {
  if (!newItemNeedsSchedule.value) return "";
  const currentProject = project.value;
  if (!currentProject || !newStartDate.value)
    return newKind.value === "milestone"
      ? "Choose a target date."
      : "Choose a start date.";
  if (
    newStartDate.value < currentProject.startDate ||
    newStartDate.value > currentProject.targetEndDate
  )
    return `Choose a date between ${fullDate(currentProject.startDate)} and ${fullDate(currentProject.targetEndDate)}.`;
  if (
    !isProjectWorkingDay(
      newStartDate.value,
      currentProject.workingWeekdays,
      currentProject.holidays,
    )
  )
    return "Choose a working day from this project's calendar.";
  if (
    newItemUsesDuration.value &&
    (!Number.isInteger(Number(newDuration.value)) ||
      Number(newDuration.value) < 1)
  )
    return "Duration must be at least one whole working day.";
  if (
    newItemUsesDuration.value &&
    currentProject.targetPolicy === "Hard boundary" &&
    newCalculatedFinish.value > currentProject.targetEndDate
  )
    return `This work would finish after the project target on ${fullDate(currentProject.targetEndDate)}.`;
  return "";
});
const projectProgress = computed(() =>
  calculateTaskProgress(workspace.active?.tasks || []),
);
const upcomingMilestone = computed(
  () =>
    (workspace.active?.tasks || [])
      .filter(
        (task) =>
          task.type === "milestone" &&
          !["Complete", "Not applicable", "Cancelled"].includes(task.status),
      )
      .sort((left, right) =>
        (left.dueDate || "9999-12-31").localeCompare(
          right.dueDate || "9999-12-31",
        ),
      )[0],
);
const openBlockerCount = computed(
  () =>
    (workspace.active?.tasks || []).filter((task) => task.status === "Blocked")
      .length,
);
const scheduleHealth = computed(() =>
  calculateScheduleHealth({
    tasks: workspace.active?.tasks || [],
    today: todayLocalDate(),
    projectTargetEndDate: project.value?.targetEndDate || "",
  }),
);
const scheduleHealthIcon = computed(() =>
  scheduleHealth.value.level === "On track" ? CheckCircle2 : AlertTriangle,
);
const selectedTask = computed(
  () =>
    workspace.active?.tasks.find((task) => task.id === route.query.task) ||
    null,
);
const planViews = ["list", "gantt", "kanban", "calendar"];
const peerViews = [
  { id: "list", label: "List", icon: LayoutList },
  { id: "gantt", label: "Gantt", icon: GanttChartSquare },
  { id: "kanban", label: "Kanban", icon: KanbanSquare },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
];
const projectNav = computed(() => [
  { id: "overview", label: "Overview" },
  { id: "list", label: "Plan" },
  { id: "files", label: "Files" },
  { id: "risks", label: "Issues & risks" },
  ...(workspace.isInternal
    ? [
        { id: "activity", label: "Activity" },
        { id: "settings", label: "Settings" },
      ]
    : workspace.canExportProject
      ? [{ id: "reports", label: "Reports" }]
      : []),
]);

const filteredTasks = computed(() =>
  filterPlanTasks(
    workspace.active?.tasks || [],
    workspace.filters,
    workspace.user?.id,
    todayLocalDate(),
    project.value?.targetEndDate || "",
  ),
);
const hasCustomStatusFilter = computed(() =>
  customStatusFilterIsActive(workspace.filters.statuses),
);
const statusFilterSummary = computed(() => {
  const count = workspace.filters.statuses.length;
  if (!hasCustomStatusFilter.value) return "All standard statuses";
  if (!count) return "No standard statuses";
  return `${count} selected`;
});
const activeFilterCount = computed(
  () =>
    Number(workspace.filters.mine) +
    Number(workspace.filters.organization !== "All organizations") +
    Number(workspace.filters.milestones) +
    Number(workspace.filters.attention) +
    Number(workspace.filters.hideInternal) +
    Number(hasCustomStatusFilter.value) +
    Number(workspace.filters.showNotApplicable) +
    Number(!!workspace.filters.query),
);
const ownerOrganizations = computed(() =>
  planTaskOrganizationOptions(
    workspace.active?.tasks || [],
    workspace.active?.project.clientOrganizationId
      ? workspace.active.project.client
      : undefined,
  ),
);
const bulkSelectedTasks = computed(() =>
  (workspace.active?.tasks || []).filter((task) =>
    selectedTaskIds.value.includes(task.id),
  ),
);
const bulkIncludesTaskCollection = computed(() =>
  bulkSelectedTasks.value.some((task) => task.type === "summary"),
);
const bulkSelectedTaskCollectionCount = computed(
  () =>
    bulkSelectedTasks.value.filter((task) => task.type === "summary").length,
);
const bulkEditableSelectedTasks = computed(() =>
  bulkSelectedTasks.value.filter((task) => task.type !== "summary"),
);
const bulkEligibleOwners = computed(() => {
  const needsInternalOwner = bulkEditableSelectedTasks.value.some(
    (task) => task.visibility === "Internal",
  );
  return (workspace.active?.participants || []).filter(
    (participant) => !needsInternalOwner || participant.kind === "internal",
  );
});
const bulkEligibleSections = computed(() => {
  const includesShared = bulkEditableSelectedTasks.value.some(
    (task) => task.visibility === "Shared",
  );
  return (workspace.active?.sections || []).filter(
    (section) => !includesShared || section.visibility !== "Internal",
  );
});
const bulkChanges = computed<BulkTaskEditChanges>(() => ({
  ...(bulkOwner.value
    ? {
        ownerActorId:
          bulkOwner.value === "__unassigned__" ? null : bulkOwner.value,
      }
    : {}),
  ...(bulkSection.value ? { sectionId: bulkSection.value } : {}),
  ...(bulkStartDate.value ? { startDate: bulkStartDate.value } : {}),
  ...(bulkStatus.value && bulkStatus.value !== "__delete__"
    ? { status: bulkStatus.value }
    : {}),
}));
const hasBulkChanges = computed(
  () =>
    bulkStatus.value === "__delete__" ||
    (bulkEditableSelectedTasks.value.length > 0 &&
      Object.keys(bulkChanges.value).length > 0),
);
const bulkDeleteSelected = computed(() =>
  bulkSelectedTasks.value.map((task) => ({
    id: task.id,
    title: task.title,
    type: task.type,
  })),
);
const localBulkDeleteBlockers = computed(() =>
  taskDeletionBlockers(workspace.active?.tasks || [], selectedTaskIds.value),
);
const displayedBulkDeleteBlockers = computed(() =>
  bulkDeleteServerBlockers.value.length
    ? bulkDeleteServerBlockers.value
    : localBulkDeleteBlockers.value,
);
const bulkStatusOptions: TaskStatus[] = [
  "Not started",
  "In progress",
  "Blocked",
  "In review",
  "Complete",
  "Not applicable",
  "Cancelled",
];

watch(
  projectId,
  async (id) => {
    expandedPlanSectionIds.value = [];
    attentionExpansionPending.value = route.query.attention === "1";
    await workspace.loadProject(id);
  },
  { immediate: true },
);
watch(
  () => !!workspace.user,
  async (ready) => {
    if (ready) await workspace.loadProject(projectId.value);
  },
);
watch(bulkStatus, (status) => {
  if (status !== "__delete__") return;
  bulkOwner.value = "";
  bulkSection.value = "";
  bulkStartDate.value = "";
  bulkDeleteError.value = "";
  bulkDeleteServerBlockers.value = [];
  bulkDeleteOpen.value = true;
});
watch(selectedTaskIds, (ids) => {
  if (!ids.length) bulkDeleteOpen.value = false;
  bulkDeleteServerBlockers.value = [];
});
watch(
  () => route.query.attention,
  (attention) => {
    if (attention === "1") {
      clearFilters();
      workspace.filters.attention = true;
      attentionExpansionPending.value = true;
    }
  },
  { immediate: true },
);
watch(
  [() => workspace.loading, filteredTasks],
  ([loading, tasks]) => {
    if (loading || !attentionExpansionPending.value) return;
    const sectionIds = new Set(
      tasks.map((task) =>
        task.type === "milestone" ? "milestones-aggregate" : task.sectionId,
      ),
    );
    expandedPlanSectionIds.value = [...sectionIds];
    attentionExpansionPending.value = false;
  },
  { flush: "post" },
);
watch(
  [
    view,
    () => workspace.isInternal,
    () => workspace.canExportProject,
    () => Boolean(workspace.active && workspace.projectAccess),
  ],
  ([currentView, internal, canExport, ready]) => {
    const cannotOpenInternalView =
      !internal && (currentView === "activity" || currentView === "settings");
    const cannotOpenReports = currentView === "reports" && !canExport;
    if (ready && (cannotOpenInternalView || cannotOpenReports))
      void router.replace(`/projects/${projectId.value}/overview`);
  },
  { immediate: true },
);
watch(quickAdd, async (value) => {
  if (value) {
    quickAddReturnFocus = document.activeElement as HTMLElement;
    priorBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    newVisibility.value = "Shared";
    const currentProject = workspace.active?.project;
    if (currentProject) {
      const today = todayLocalDate();
      const candidate =
        today >= currentProject.startDate &&
        today <= currentProject.targetEndDate
          ? today
          : currentProject.startDate;
      const workingDate = addProjectWorkingDays(
        candidate,
        0,
        currentProject.workingWeekdays,
        currentProject.holidays,
      );
      newStartDate.value =
        workingDate <= currentProject.targetEndDate
          ? workingDate
          : currentProject.startDate;
    }
    newDuration.value = 5;
    await nextTick(() => quickInput.value?.focus());
  } else {
    document.body.style.overflow = priorBodyOverflow;
    quickAddReturnFocus?.focus();
  }
});
watch(
  [bulkOwner, bulkSection, bulkStartDate, bulkStatus, selectedTaskIds],
  () => {
    bulkPreview.value = null;
    bulkError.value = "";
  },
  { deep: true },
);
watch(view, (nextView) => {
  if (nextView === "list" || !bulkMode.value) return;
  bulkMode.value = false;
  selectedTaskIds.value = [];
});
watch(
  () => workspace.active?.sections.map((section) => section.id),
  (ids) => {
    if (ids?.length && !ids.includes(newSection.value))
      newSection.value = ids[0]!;
  },
  { immediate: true },
);
onMounted(() => {
  const profile = sessionStorage.getItem("planner-demo-profile") as
    "internal" | "admin" | "client" | null;
  if (!workspace.user && profile) workspace.login(profile);
});
onBeforeUnmount(() => {
  document.body.style.overflow = priorBodyOverflow;
});

function go(viewId: string) {
  router.push({
    path: `/projects/${projectId.value}/${viewId}`,
    query: viewId === view.value ? route.query : {},
  });
}
function openSettings(section: string, focus?: string) {
  router.push({
    path: `/projects/${projectId.value}/settings`,
    query: {
      settings: section,
      ...(focus ? { focus } : {}),
    },
  });
}
function openTask(id: string, panel = "overview", status?: TaskStatus) {
  pendingTaskStatus.value = status;
  router.push({ query: { ...route.query, task: id, panel } });
}
function startAdd(sectionId?: string) {
  if (sectionId) newSection.value = sectionId;
  filterOpen.value = false;
  quickAdd.value = true;
}
function closeQuickAdd() {
  quickAdd.value = false;
}
function trapQuickAddKeys(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    closeQuickAdd();
    return;
  }
  if (event.key !== "Tab" || !quickDialog.value) return;
  const items = [
    ...quickDialog.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
  if (!items.length) return;
  if (event.shiftKey && document.activeElement === items[0]) {
    event.preventDefault();
    items.at(-1)?.focus();
  } else if (!event.shiftKey && document.activeElement === items.at(-1)) {
    event.preventDefault();
    items[0]?.focus();
  }
}
function closeTask() {
  pendingTaskStatus.value = undefined;
  const query = { ...route.query };
  delete query.task;
  delete query.panel;
  router.replace({ query });
}
async function addItem() {
  if (!newTitle.value.trim()) return;
  if (newKind.value === "section") {
    await workspace.createSection({
      name: newTitle.value.trim(),
      visibility: "Shared",
    });
    newTitle.value = "";
    quickAdd.value = false;
    return;
  }
  if (newScheduleError.value) return;
  const task = await workspace.createTask({
    title: newTitle.value.trim(),
    sectionId: newSection.value,
    visibility: workspace.isInternal ? newVisibility.value : "Shared",
    type: newKind.value,
    ...(newItemNeedsSchedule.value
      ? {
          startDate: newStartDate.value,
          dueDate:
            newKind.value === "milestone"
              ? newStartDate.value
              : newCalculatedFinish.value,
          duration: newItemUsesDuration.value ? Number(newDuration.value) : 0,
        }
      : {}),
  });
  newTitle.value = "";
  quickAdd.value = false;
  if (task) openTask(task.id);
}
function clearFilters() {
  workspace.filters = {
    mine: false,
    organization: "All organizations",
    milestones: false,
    attention: false,
    hideInternal: false,
    statuses: [...DEFAULT_VISIBLE_STATUSES],
    showNotApplicable: false,
    query: "",
  };
  filterOpen.value = false;
}
function resetBulkFields() {
  bulkOwner.value = "";
  bulkSection.value = "";
  bulkStartDate.value = "";
  bulkStatus.value = "";
  bulkPreview.value = null;
  bulkError.value = "";
  bulkDeleteOpen.value = false;
  bulkDeleteError.value = "";
  bulkDeleteServerBlockers.value = [];
}
function showScheduleAttention() {
  clearFilters();
  workspace.filters.attention = scheduleHealth.value.level !== "On track";
  attentionExpansionPending.value = scheduleHealth.value.level !== "On track";
  void router.push({
    path: `/projects/${projectId.value}/list`,
    query: scheduleHealth.value.level === "On track" ? {} : { attention: "1" },
  });
}
function toggleBulkManage() {
  if (bulkMode.value) {
    bulkMode.value = false;
    selectedTaskIds.value = [];
    resetBulkFields();
    return;
  }
  bulkMode.value = true;
  filterOpen.value = false;
  if (view.value !== "list") go("list");
}
function toggleTaskSelection(taskId: string) {
  if (selectedTaskIds.value.includes(taskId)) {
    selectedTaskIds.value = selectedTaskIds.value.filter((id) => id !== taskId);
    return;
  }
  if (selectedTaskIds.value.length >= BULK_SELECTION_LIMIT) return;
  selectedTaskIds.value = [...selectedTaskIds.value, taskId];
}
function clearBulkSelection() {
  selectedTaskIds.value = [];
}
function selectVisibleTasks(taskIds: string[], selected: boolean) {
  const next = new Set(selectedTaskIds.value);
  taskIds.forEach((taskId) =>
    selected && next.size < BULK_SELECTION_LIMIT
      ? next.add(taskId)
      : !selected
        ? next.delete(taskId)
        : undefined,
  );
  selectedTaskIds.value = [...next];
}
async function applyBulkChanges() {
  if (!selectedTaskIds.value.length || !hasBulkChanges.value) return;
  if (bulkStatus.value === "__delete__") {
    bulkDeleteError.value = "";
    bulkDeleteOpen.value = true;
    return;
  }
  if (bulkPreview.value) {
    await applyBulkEdit(bulkPreview.value);
    return;
  }

  bulkChecking.value = true;
  bulkError.value = "";
  try {
    const preview = await workspace.previewBulkTaskEdit(
      selectedTaskIds.value,
      bulkChanges.value,
    );

    if (requiresBulkScheduleReview(preview.scheduleImpacts)) {
      bulkPreview.value = preview;
      return;
    }

    await applyBulkEdit(preview);
  } catch (previewError) {
    bulkError.value =
      previewError instanceof Error
        ? previewError.message
        : "The changes could not be applied. No tasks were changed.";
  } finally {
    bulkChecking.value = false;
  }
}
async function confirmBulkDeletion() {
  if (
    !selectedTaskIds.value.length ||
    bulkDeleting.value ||
    displayedBulkDeleteBlockers.value.length
  )
    return;
  bulkDeleting.value = true;
  bulkDeleteError.value = "";
  try {
    await workspace.deleteTasks(selectedTaskIds.value, "delete");
    bulkDeleteOpen.value = false;
    bulkMode.value = false;
    selectedTaskIds.value = [];
    resetBulkFields();
  } catch (error) {
    if (error instanceof ApiError)
      bulkDeleteServerBlockers.value = taskDeletionBlockersFromDetails(
        error.details,
      );
    bulkDeleteError.value =
      error instanceof Error
        ? error.message
        : "No tasks were deleted. Review the selection and try again.";
  } finally {
    bulkDeleting.value = false;
  }
}
async function applyBulkEdit(preview: BulkTaskEditPreview) {
  bulkApplying.value = true;
  bulkError.value = "";
  try {
    await workspace.applyBulkTaskEdit(preview, bulkChanges.value);
    bulkMode.value = false;
    selectedTaskIds.value = [];
    resetBulkFields();
  } catch (applyError) {
    bulkError.value =
      applyError instanceof Error
        ? applyError.message
        : "No tasks were changed. Try applying the changes again.";
  } finally {
    bulkApplying.value = false;
  }
}
</script>

<template>
  <AppShell project-mode>
    <div
      v-if="workspace.loading && !workspace.active"
      class="project-loading"
      aria-label="Loading project"
    >
      <div class="skeleton"></div>
      <div class="skeleton"></div>
      <div class="skeleton"></div>
      <div class="skeleton"></div>
    </div>
    <div v-else-if="!workspace.active" class="project-error">
      <AlertTriangle :size="28" />
      <h1>We could not open this project.</h1>
      <p>Your work is safe. Return to Projects and try again.</p>
      <button class="button button--primary" @click="router.push('/projects')">
        <ArrowLeft :size="16" /> Return to projects
      </button>
    </div>
    <div
      v-else
      class="project-workspace"
      :class="{ 'project-workspace--plan': planViews.includes(view) }"
    >
      <header class="project-header">
        <div class="project-title-row">
          <button
            class="icon-button project-back"
            type="button"
            aria-label="Return to projects"
            @click="router.push('/projects')"
          >
            <ArrowLeft :size="18" /></button
          ><span class="client-logo">
            <img
              v-if="project?.clientOrganizationLogoDataUrl"
              :src="project.clientOrganizationLogoDataUrl"
              :alt="`${project.client} logo`"
            />
            <template v-else>{{
              project?.client.slice(0, 2).toUpperCase() || "—"
            }}</template>
          </span>
          <div class="project-title">
            <div>
              <span>{{ project?.code }}</span
              ><span
                v-if="workspace.liveState !== 'live'"
                class="live-indicator"
                :class="`live-indicator--${workspace.liveState}`"
                role="status"
                :title="
                  workspace.liveState === 'paused'
                    ? 'Your open draft is safe. Check your connection to resume live project updates.'
                    : 'Committed project changes appear here automatically.'
                "
                ><i aria-hidden="true"></i
                >{{
                  workspace.liveState === "paused"
                    ? "Updates paused"
                    : "Connecting to updates"
                }}</span
              >
            </div>
            <h1>{{ project?.name }}</h1>
          </div>
          <div class="project-header__actions">
            <component
              :is="workspace.isInternal ? 'button' : 'div'"
              class="project-participants"
              :type="workspace.isInternal ? 'button' : undefined"
              :role="workspace.isInternal ? undefined : 'group'"
              :aria-label="
                workspace.isInternal
                  ? 'Review project participants'
                  : 'Project participants'
              "
              @click="workspace.isInternal && openSettings('people')"
            >
              <PersonAvatar
                v-for="person in headerParticipants"
                :key="person.id"
                :name="person.name"
                :initials="person.initials"
                :photo-url="person.photoUrl"
              />
              <span
                v-if="remainingHeaderParticipants.length"
                class="participant-overflow"
                role="img"
                :aria-label="`And ${remainingHeaderParticipants.length} more participants: ${remainingHeaderParticipants.map((person) => person.name).join(', ')}`"
                :title="
                  remainingHeaderParticipants
                    .map((person) => person.name)
                    .join(', ')
                "
              >
                +{{ remainingHeaderParticipants.length }}
              </span>
            </component>
            <button
              v-if="workspace.canManageProject"
              class="button button--secondary bulk-manage-trigger"
              type="button"
              :aria-pressed="bulkMode"
              :title="
                bulkMode
                  ? 'Finish bulk management'
                  : 'Select tasks to update together'
              "
              @click="toggleBulkManage"
            >
              <ListChecks :size="16" />
              <span>{{ bulkMode ? "Done" : "Bulk manage" }}</span>
            </button>
            <button
              v-if="workspace.isInternal"
              class="icon-button project-settings-button"
              type="button"
              aria-label="Open project settings"
              title="Project settings"
              @click="openSettings('details')"
            >
              <SettingsIcon :size="18" />
            </button>
          </div>
        </div>
        <div class="health-band">
          <button
            class="health-summary"
            type="button"
            :title="scheduleHealth.reason"
            :aria-label="`${scheduleHealth.level} schedule health. ${scheduleHealth.reason} View schedule details in the plan.`"
            @click="showScheduleAttention"
          >
            <span
              class="health-mark"
              :class="`health-mark--${scheduleHealth.level.toLowerCase().replaceAll(' ', '-')}`"
              ><component :is="scheduleHealthIcon" :size="15" /></span
            ><span
              ><small>Schedule health</small
              ><strong>{{ scheduleHealth.level }}</strong></span
            >
          </button>
          <div>
            <CircleDotDashed :size="17" /><span
              ><small>Progress</small
              ><strong>{{ projectProgress.progress }}% complete</strong></span
            >
          </div>
          <div>
            <Flag :size="17" /><span
              ><small>Next milestone</small
              ><strong
                >{{ upcomingMilestone?.title || "No upcoming milestones"
                }}<template v-if="upcomingMilestone">
                  · {{ shortDate(upcomingMilestone.dueDate) }}</template
                ></strong
              ></span
            >
          </div>
          <div>
            <CalendarDays :size="17" /><span
              ><small>Project target</small
              ><strong>{{
                fullDate(project?.targetEndDate || "")
              }}</strong></span
            >
          </div>
          <button type="button" @click="showScheduleAttention">
            <strong>{{ openBlockerCount }}</strong
            ><span>open blockers</span>
          </button>
        </div>
        <nav class="project-nav" aria-label="Project navigation">
          <button
            v-for="item in projectNav"
            :key="item.id"
            type="button"
            :class="{
              active:
                item.id === view ||
                (item.id === 'list' && planViews.includes(view)),
            }"
            @click="go(item.id)"
          >
            {{ item.label }}
          </button>
        </nav>
        <div v-if="workspace.error" class="workspace-alert" role="alert">
          <span>{{ workspace.error }}</span>
          <button
            class="icon-button"
            type="button"
            aria-label="Dismiss message"
            @click="workspace.error = ''"
          >
            <X :size="17" />
          </button>
        </div>
      </header>

      <div v-if="planViews.includes(view)" class="view-toolbar">
        <div class="view-tabs" role="tablist" aria-label="Plan view">
          <button
            v-for="item in peerViews"
            :key="item.id"
            type="button"
            role="tab"
            :aria-selected="view === item.id"
            :class="{ active: view === item.id }"
            @click="go(item.id)"
          >
            <component :is="item.icon" :size="16" />{{ item.label }}
          </button>
        </div>
        <div class="view-actions">
          <div class="inline-search">
            <Search :size="15" /><input
              v-model="workspace.filters.query"
              aria-label="Search visible work"
              placeholder="Find work"
            />
          </div>
          <button
            class="button button--secondary"
            :class="{ 'button--filter-active': activeFilterCount }"
            type="button"
            aria-haspopup="dialog"
            :aria-expanded="filterOpen"
            @click="filterOpen = !filterOpen"
          >
            <Filter :size="15" /> Filters
            <span v-if="activeFilterCount">{{
              activeFilterCount
            }}</span></button
          ><button
            class="button button--secondary preset-button"
            :class="{ 'button--filter-active': workspace.filters.milestones }"
            type="button"
            :aria-pressed="workspace.filters.milestones"
            @click="
              workspace.filters.milestones = !workspace.filters.milestones;
              workspace.filters.attention = false;
            "
          >
            <Flag :size="14" /> Milestones</button
          ><button
            class="button button--secondary preset-button"
            :class="{ 'button--filter-active': workspace.filters.attention }"
            type="button"
            :aria-pressed="workspace.filters.attention"
            @click="
              workspace.filters.attention = !workspace.filters.attention;
              workspace.filters.milestones = false;
            "
          >
            <AlertTriangle :size="14" /> Needs attention</button
          ><button
            class="button button--secondary preset-button"
            :class="{ 'button--filter-active': workspace.filters.mine }"
            type="button"
            :aria-pressed="workspace.filters.mine"
            @click="workspace.filters.mine = !workspace.filters.mine"
          >
            <Check v-if="workspace.filters.mine" :size="14" /> My work</button
          ><button
            v-if="workspace.canEditPlan"
            class="button button--primary"
            type="button"
            aria-haspopup="dialog"
            :aria-expanded="quickAdd"
            :disabled="workspace.loading"
            :title="
              workspace.loading
                ? 'Wait for the project plan to finish loading.'
                : undefined
            "
            @click="startAdd()"
          >
            <Plus :size="16" /> Add work
          </button>
        </div>
        <div
          v-if="filterOpen"
          class="filter-popover"
          role="dialog"
          aria-label="Filter visible work"
        >
          <header>
            <strong>Filter visible work</strong
            ><button
              class="icon-button"
              type="button"
              aria-label="Close filters"
              @click="filterOpen = false"
            >
              <X :size="17" />
            </button>
          </header>
          <label class="filter-check"
            ><input v-model="workspace.filters.mine" type="checkbox" />
            <span>Only my work</span></label
          >
          <label class="filter-check filter-check--described">
            <input v-model="workspace.filters.attention" type="checkbox" />
            <span
              ><strong>Needs schedule attention</strong
              ><small
                >Blocked, overdue, or scheduled beyond the project
                target.</small
              ></span
            >
          </label>
          <details class="filter-disclosure">
            <summary>
              <span>Status</span><small>{{ statusFilterSummary }}</small>
            </summary>
            <div class="filter-options">
              <label v-for="status in DEFAULT_VISIBLE_STATUSES" :key="status">
                <input
                  v-model="workspace.filters.statuses"
                  type="checkbox"
                  :value="status"
                />
                <span>{{ status }}</span>
              </label>
            </div>
          </details>
          <label class="filter-check filter-check--described"
            ><input
              v-model="workspace.filters.showNotApplicable"
              type="checkbox"
            />
            <span
              ><strong>Show not applicable</strong
              ><small>These tasks are hidden by default.</small></span
            ></label
          >
          <label
            v-if="workspace.isInternal"
            class="filter-check filter-check--described"
          >
            <input v-model="workspace.filters.hideInternal" type="checkbox" />
            <span
              ><strong>Hide Internal work</strong
              ><small
                >Useful when presenting the client-ready plan.</small
              ></span
            ></label
          >
          <div v-if="workspace.isInternal" class="field">
            <label for="filter-organization">Owner organization</label
            ><select
              id="filter-organization"
              v-model="workspace.filters.organization"
              class="select"
            >
              <option>All organizations</option>
              <option
                v-for="organization in ownerOrganizations"
                :key="organization"
              >
                {{ organization }}
              </option>
            </select>
          </div>
          <footer>
            <button
              class="button button--quiet"
              type="button"
              @click="clearFilters"
            >
              Clear filters</button
            ><button
              class="button button--primary"
              type="button"
              @click="filterOpen = false"
            >
              Show work
            </button>
          </footer>
        </div>
      </div>
      <div
        class="project-canvas"
        :class="{ 'project-canvas--wide': planViews.includes(view) }"
      >
        <OverviewPanel v-if="view === 'overview'" />
        <div v-else-if="view === 'list'" class="list-layout">
          <div class="list-scroll">
            <PlanList
              :tasks="filteredTasks"
              :sections="workspace.active.sections"
              :progress-tasks="workspace.active.tasks"
              :hide-empty-sections="true"
              :selection-mode="bulkMode"
              :selected-task-ids="selectedTaskIds"
              :selection-limit="BULK_SELECTION_LIMIT"
              v-model:expanded-section-ids="expandedPlanSectionIds"
              @add-item="startAdd"
              @open-task="openTask"
              @toggle-task-selection="toggleTaskSelection"
              @select-visible-tasks="selectVisibleTasks"
            />
          </div>
          <section
            v-if="bulkMode && selectedTaskIds.length"
            class="bulk-edit-dock"
            role="region"
            aria-label="Bulk work changes"
          >
            <p v-if="bulkError" class="bulk-edit-error" role="alert">
              {{ bulkError }}
            </p>
            <p v-if="bulkIncludesTaskCollection" class="bulk-edit-guidance">
              <template v-if="bulkEditableSelectedTasks.length">
                {{ bulkSelectedTaskCollectionCount }} task collection{{
                  bulkSelectedTaskCollectionCount === 1 ? "" : "s"
                }}
                will be ignored. Changes apply to the remaining
                {{ bulkEditableSelectedTasks.length }} work item{{
                  bulkEditableSelectedTasks.length === 1 ? "" : "s"
                }}.
              </template>
              <template v-else>
                Task collections calculate these fields from their child work.
                Select a task or milestone to change fields, or choose Delete
                Task from Status.
              </template>
            </p>
            <div
              v-if="bulkPreview?.scheduleImpacts.length"
              class="bulk-schedule-impact"
              role="region"
              aria-label="Schedule changes"
              tabindex="0"
            >
              <table>
                <caption>
                  Tasks and milestones that will move
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Work</th>
                    <th scope="col">Change</th>
                    <th scope="col">Start</th>
                    <th scope="col">Finish</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="impact in bulkPreview.scheduleImpacts"
                    :key="impact.taskId"
                  >
                    <th scope="row">{{ impact.title }}</th>
                    <td>
                      {{
                        impact.kind === "summary"
                          ? "Calculated"
                          : impact.direct
                            ? "Selected"
                            : "Dependent"
                      }}
                    </td>
                    <td>
                      {{ shortDate(impact.oldStartDate) }} →
                      {{ shortDate(impact.newStartDate) }}
                    </td>
                    <td>
                      {{ shortDate(impact.oldDueDate) }} →
                      {{ shortDate(impact.newDueDate) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="bulk-edit-controls">
              <div class="bulk-selection-count" aria-live="polite">
                <strong>{{ selectedTaskIds.length }}</strong>
                <span>selected</span>
                <button
                  class="icon-button"
                  type="button"
                  aria-label="Clear task selection"
                  title="Clear selection"
                  :disabled="bulkApplying"
                  @click="clearBulkSelection"
                >
                  <X :size="16" aria-hidden="true" />
                </button>
              </div>
              <div class="bulk-edit-fields">
                <div class="field">
                  <label for="bulk-owner">Owner</label>
                  <select
                    id="bulk-owner"
                    v-model="bulkOwner"
                    class="select"
                    :disabled="
                      bulkApplying ||
                      bulkStatus === '__delete__' ||
                      !bulkEditableSelectedTasks.length
                    "
                  >
                    <option value="">No change</option>
                    <option value="__unassigned__">Unassigned</option>
                    <option
                      v-for="person in bulkEligibleOwners"
                      :key="person.id"
                      :value="person.id"
                    >
                      {{ person.name }} · {{ person.organization }}
                    </option>
                  </select>
                </div>
                <div class="field">
                  <label for="bulk-section">Section</label>
                  <select
                    id="bulk-section"
                    v-model="bulkSection"
                    class="select"
                    :disabled="
                      bulkApplying ||
                      bulkStatus === '__delete__' ||
                      !bulkEditableSelectedTasks.length
                    "
                  >
                    <option value="">No change</option>
                    <option
                      v-for="section in bulkEligibleSections"
                      :key="section.id"
                      :value="section.id"
                    >
                      {{ section.name }}
                    </option>
                  </select>
                </div>
                <div class="field bulk-start-field">
                  <label for="bulk-start">Start date</label>
                  <input
                    id="bulk-start"
                    v-model="bulkStartDate"
                    class="input"
                    type="date"
                    :min="workspace.active?.project.startDate"
                    :max="workspace.active?.project.targetEndDate"
                    :disabled="
                      bulkApplying ||
                      bulkStatus === '__delete__' ||
                      !bulkEditableSelectedTasks.length
                    "
                  />
                </div>
                <div class="field">
                  <label for="bulk-status">Status</label>
                  <select
                    id="bulk-status"
                    v-model="bulkStatus"
                    class="select"
                    :disabled="bulkApplying"
                  >
                    <option value="">No change</option>
                    <option
                      v-for="status in bulkStatusOptions"
                      :key="status"
                      :disabled="!bulkEditableSelectedTasks.length"
                    >
                      {{ status }}
                    </option>
                    <option
                      v-if="workspace.canManageProject"
                      value="__delete__"
                    >
                      Delete Task
                    </option>
                  </select>
                </div>
              </div>
              <button
                class="button bulk-edit-action"
                :class="
                  bulkStatus === '__delete__'
                    ? 'button--danger'
                    : 'button--primary'
                "
                type="button"
                :disabled="!hasBulkChanges || bulkChecking || bulkApplying"
                @click="applyBulkChanges"
              >
                {{
                  bulkApplying
                    ? "Applying…"
                    : bulkChecking
                      ? "Checking…"
                      : bulkStatus === "__delete__"
                        ? "Review deletion"
                        : "Apply changes"
                }}
                <Trash2
                  v-if="bulkStatus === '__delete__'"
                  :size="15"
                  aria-hidden="true"
                />
              </button>
            </div>
            <span v-if="bulkPreview" class="sr-only" role="status">
              {{ bulkPreview.selectedCount }} selected.
              <template v-if="bulkPreview.excludedTasks.length">
                {{ bulkPreview.excludedTasks.length }} task collection{{
                  bulkPreview.excludedTasks.length === 1 ? " was" : "s were"
                }}
                ignored.
              </template>
              <template
                v-if="bulkPreview.affectedCount > bulkPreview.selectedCount"
              >
                {{ bulkPreview.affectedCount - bulkPreview.selectedCount }}
                dependent task{{
                  bulkPreview.affectedCount - bulkPreview.selectedCount === 1
                    ? ""
                    : "s"
                }}
                will also move.
              </template>
              Changes are ready to apply.
            </span>
          </section>
        </div>
        <GanttBoard
          v-else-if="view === 'gantt'"
          :tasks="filteredTasks"
          :sections="workspace.active.sections"
          @open-task="openTask"
        />
        <KanbanBoard
          v-else-if="view === 'kanban'"
          :tasks="filteredTasks"
          @open-task="openTask"
        />
        <CalendarBoard
          v-else-if="view === 'calendar'"
          :tasks="filteredTasks"
          @open-task="openTask"
        />
        <FilesPanel v-else-if="view === 'files'" />
        <RisksPanel v-else-if="view === 'risks'" />
        <ActivityPanel
          v-else-if="view === 'activity' && workspace.isInternal"
        />
        <SettingsPanel
          v-else-if="view === 'settings' && workspace.isInternal"
        />
        <SettingsPanel
          v-else-if="view === 'reports' && workspace.canExportProject"
        />
      </div>
      <TaskDetail
        :open="!!selectedTask"
        :task="selectedTask"
        :initial-section="String(route.query.panel || 'overview')"
        :initial-status="pendingTaskStatus"
        @close="closeTask"
        @open-task="openTask"
      />
      <TaskDeleteDialog
        :open="bulkDeleteOpen"
        :tasks="bulkDeleteSelected"
        :blockers="displayedBulkDeleteBlockers"
        :deleting="bulkDeleting"
        :error="bulkDeleteError"
        @cancel="bulkDeleteOpen = false"
        @confirm="confirmBulkDeletion"
      />
    </div>
    <Teleport to="body">
      <div v-if="quickAdd && workspace.active" class="add-work-layer">
        <button
          class="add-work-scrim"
          type="button"
          aria-label="Cancel adding work"
          @click="closeQuickAdd"
        ></button>
        <section
          ref="quickDialog"
          class="add-work-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-work-title"
          aria-describedby="add-work-description"
          @keydown="trapQuickAddKeys"
        >
          <form
            class="add-work-dialog__form"
            aria-label="Add work"
            @submit.prevent="addItem"
          >
            <header class="add-work-dialog__header">
              <div>
                <h2 id="add-work-title">Add work</h2>
                <p id="add-work-description">
                  <template v-if="newItemNeedsSchedule"
                    >Set the name and schedule before adding it to the
                    plan.</template
                  ><template v-else
                    >Name this {{ newItemLabel.toLowerCase() }}.</template
                  >
                </p>
              </div>
              <button
                class="icon-button"
                type="button"
                aria-label="Close Add work"
                @click="closeQuickAdd"
              >
                <X :size="20" />
              </button>
            </header>
            <div class="add-work-dialog__body">
              <div class="field add-work-dialog__type">
                <label for="new-item-type">Item type</label>
                <select id="new-item-type" v-model="newKind" class="select">
                  <option value="task">Task</option>
                  <option value="checklist">Checklist</option>
                  <option value="questionnaire">Questionnaire</option>
                  <option value="summary">Task collection</option>
                  <option value="milestone">Milestone</option>
                  <option value="section">Section</option>
                </select>
              </div>
              <div class="field add-work-dialog__name">
                <label for="new-item-name">{{ newItemLabel }} name</label>
                <input
                  id="new-item-name"
                  ref="quickInput"
                  v-model="newTitle"
                  class="input"
                  :placeholder="`Name the ${newItemLabel.toLowerCase()}`"
                  required
                />
              </div>
              <div v-if="newItemNeedsSchedule" class="field">
                <label for="new-item-date">
                  {{ newKind === "milestone" ? "Target date" : "Start date" }}
                </label>
                <input
                  id="new-item-date"
                  v-model="newStartDate"
                  class="input"
                  type="date"
                  :min="project?.startDate"
                  :max="project?.targetEndDate"
                  :aria-invalid="newScheduleError ? 'true' : undefined"
                  :aria-describedby="
                    newScheduleError ? 'new-work-schedule-error' : undefined
                  "
                />
              </div>
              <div v-if="newItemUsesDuration" class="field">
                <label for="new-item-duration">Duration</label>
                <div class="input-with-unit">
                  <input
                    id="new-item-duration"
                    v-model.number="newDuration"
                    class="input"
                    type="number"
                    min="1"
                    step="1"
                    inputmode="numeric"
                    :aria-invalid="newScheduleError ? 'true' : undefined"
                    :aria-describedby="
                      newScheduleError ? 'new-work-schedule-error' : undefined
                    "
                  /><span>working days</span>
                </div>
              </div>
              <div v-if="newKind !== 'section'" class="field">
                <label for="new-item-section">Section</label>
                <select
                  id="new-item-section"
                  v-model="newSection"
                  class="select"
                >
                  <option
                    v-for="section in workspace.active.sections"
                    :key="section.id"
                    :value="section.id"
                  >
                    {{ section.name }}
                  </option>
                </select>
              </div>
              <label
                v-if="workspace.canManageProject && newKind !== 'section'"
                class="internal-only-option"
                for="new-item-internal"
              >
                <input
                  id="new-item-internal"
                  type="checkbox"
                  :checked="newVisibility === 'Internal'"
                  @change="
                    newVisibility = ($event.target as HTMLInputElement).checked
                      ? 'Internal'
                      : 'Shared'
                  "
                />
                <span
                  ><strong>Internal</strong
                  ><small
                    >Only VIPdesk project participants can see this work.</small
                  ></span
                >
              </label>
            </div>
            <footer class="add-work-dialog__footer">
              <div class="add-work-dialog__status">
                <p
                  v-if="newScheduleError"
                  id="new-work-schedule-error"
                  class="form-error"
                  role="alert"
                >
                  {{ newScheduleError }}
                </p>
                <p v-else-if="newItemUsesDuration">
                  Finishes {{ fullDate(newCalculatedFinish) }}
                </p>
              </div>
              <div class="add-work-dialog__actions">
                <button
                  class="button button--secondary"
                  type="button"
                  @click="closeQuickAdd"
                >
                  Cancel
                </button>
                <button
                  class="button button--primary"
                  :disabled="!newTitle.trim() || !!newScheduleError"
                  type="submit"
                >
                  {{
                    newKind === "section"
                      ? "Add section"
                      : newKind === "summary"
                        ? "Add task collection"
                        : newKind === "milestone"
                          ? "Add milestone"
                          : newKind === "checklist"
                            ? "Add checklist"
                            : newKind === "questionnaire"
                              ? "Add questionnaire"
                              : "Add task"
                  }}
                </button>
              </div>
            </footer>
          </form>
        </section>
      </div>
    </Teleport>
  </AppShell>
</template>

<style scoped>
.project-workspace {
  min-width: 0;
  height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background: var(--sheet);
}
.project-workspace--plan {
  grid-template-rows: auto auto minmax(0, 1fr);
}
.project-header {
  background: white;
}
.workspace-alert {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 14px;
  border-bottom: 1px solid #ddb5b5;
  background: var(--danger-soft);
  color: var(--danger);
  font-size: 13px;
  font-weight: 650;
}
.project-title-row {
  min-height: 72px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 18px;
  border-bottom: 1px solid var(--rule);
}
.project-back {
  margin-right: -3px;
}
.client-logo {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 1px solid var(--rule-strong);
  border-radius: 5px;
  background: var(--sheet-subtle);
  color: var(--ink-800);
  font-size: 11px;
  font-weight: 850;
  overflow: hidden;
}
.client-logo img {
  width: 100%;
  height: 100%;
  padding: 3px;
  object-fit: contain;
}
.project-settings-button {
  width: 36px;
  height: 36px;
  border: 1px solid var(--rule-strong);
  background: var(--sheet);
  color: var(--text-soft);
}
.bulk-manage-trigger[aria-pressed="true"] {
  border-color: var(--brand-strong);
  background: var(--brand-soft);
  color: var(--brand-strong);
}
.project-title {
  min-width: 0;
  flex: 1 1 auto;
}
.project-title > div {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--text-faint);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--text-soft);
  letter-spacing: 0;
  text-transform: none;
}
.live-indicator i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--warning);
}
.live-indicator--live i {
  background: var(--brand-strong);
}
.live-indicator--paused {
  color: var(--danger);
}
.live-indicator--paused i {
  background: var(--danger);
}
.project-title h1 {
  margin-top: 2px;
  font-size: 21px;
}
.project-header__actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
}
.bulk-edit-dock {
  display: grid;
  border-top: 1px solid var(--rule-strong);
  background: var(--sheet);
}
.bulk-edit-controls {
  min-width: 0;
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr) max-content;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 9px;
}
.bulk-edit-fields {
  min-width: 0;
  display: grid;
  grid-template-columns:
    minmax(128px, 1.15fr) minmax(128px, 1fr)
    minmax(138px, 0.82fr) minmax(128px, 0.88fr);
  gap: 8px;
}
.bulk-selection-count {
  height: 34px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 8px;
  border-right: 1px solid var(--rule);
  color: var(--text-soft);
  font-size: 12px;
  white-space: nowrap;
}
.bulk-selection-count strong {
  color: var(--text);
  font-size: 14px;
}
.bulk-selection-count .icon-button {
  width: 28px;
  height: 28px;
  margin-left: 2px;
  color: var(--text-soft);
}
.bulk-edit-controls .field {
  min-width: 0;
  gap: 2px;
}
.bulk-edit-controls label {
  color: var(--text-soft);
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
}
.bulk-edit-controls .input,
.bulk-edit-controls .select {
  height: 34px;
  min-width: 0;
  padding-block: 5px;
  font-size: 13px;
}
.bulk-edit-action {
  min-width: 116px;
  height: 34px;
  align-self: end;
}
.bulk-edit-error {
  margin: 8px 12px 0;
  padding: 7px 9px;
  border: 1px solid #e5c7c7;
  border-radius: var(--radius-sm);
  background: var(--danger-soft);
  color: var(--danger);
  font-size: 12px;
}
.bulk-edit-guidance {
  margin: 8px 12px 0;
  color: var(--text-soft);
  font-size: 12px;
}
.bulk-schedule-impact {
  max-height: 160px;
  margin: 8px 12px 0;
  overflow: auto;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
}
.bulk-schedule-impact table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.bulk-schedule-impact caption {
  padding: 7px 9px;
  text-align: left;
  font-weight: 700;
}
.bulk-schedule-impact th,
.bulk-schedule-impact td {
  padding: 6px 9px;
  border-top: 1px solid var(--rule);
  text-align: left;
  white-space: nowrap;
}
.bulk-schedule-impact tbody th {
  font-weight: 600;
}
.project-participants {
  min-width: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 2px 5px;
  border: 1px solid transparent;
  border-radius: 5px;
  background: transparent;
  color: var(--text-soft);
}
button.project-participants {
  cursor: pointer;
}
button.project-participants:hover {
  border-color: var(--rule);
  background: var(--sheet-subtle);
}
.project-participants :deep(.avatar),
.participant-overflow {
  width: 30px;
  height: 30px;
  margin-left: -7px;
  border: 2px solid white;
}
.project-participants :deep(.avatar:first-child) {
  margin-left: 0;
}
.participant-overflow {
  display: inline-grid;
  place-items: center;
  border-radius: 50%;
  background: var(--sheet-strong);
  color: var(--text-soft);
  font-size: 10px;
  font-weight: 750;
  font-variant-numeric: tabular-nums;
}
@media (pointer: coarse) {
  button.project-participants {
    min-height: 44px;
  }
}
.health-band {
  min-height: 58px;
  display: grid;
  grid-template-columns: 0.85fr 0.9fr 1.4fr 1.15fr auto;
  align-items: center;
  border-bottom: 1px solid var(--rule);
  background: #f7f9f8;
}
.health-band > div,
.health-band > .health-summary {
  min-width: 0;
  min-height: 32px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 16px;
  border-right: 1px solid var(--rule);
  background: transparent;
  color: var(--text-soft);
}
.health-band > div > span:last-child,
.health-band > .health-summary > span:last-child {
  min-width: 0;
  display: grid;
}
.health-band > .health-summary {
  min-height: 58px;
  text-align: start;
  cursor: pointer;
}
.health-band > .health-summary:hover {
  background: var(--sheet-strong);
}
.health-band small {
  color: var(--text-faint);
  font-size: 9px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.health-band strong {
  overflow: hidden;
  color: var(--text);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.health-mark {
  width: 27px;
  height: 27px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 50%;
}
.health-mark--at-risk {
  color: var(--warning);
  background: var(--warning-soft);
}
.health-mark--off-track {
  color: var(--danger);
  background: var(--danger-soft);
}
.health-mark--on-track {
  color: var(--success);
  background: var(--success-soft);
}
.health-band > button:not(.health-summary) {
  min-height: 58px;
  display: grid;
  place-content: center;
  padding: 0 17px;
  background: white;
  color: var(--danger);
  cursor: pointer;
}
.health-band > button:not(.health-summary) span {
  color: var(--text-soft);
  font-size: 9px;
  text-transform: uppercase;
}
.project-nav {
  min-height: 42px;
  display: flex;
  align-items: end;
  gap: 2px;
  padding: 0 16px;
  border-bottom: 1px solid var(--rule);
  overflow-x: auto;
}
.project-nav button {
  position: relative;
  height: 41px;
  padding: 0 12px;
  background: transparent;
  color: var(--text-soft);
  font-size: 12px;
  font-weight: 650;
  white-space: nowrap;
  cursor: pointer;
}
.project-nav button.active {
  color: var(--text);
}
.project-nav button.active::after {
  content: "";
  position: absolute;
  inset: auto 9px -1px;
  height: 2px;
  background: var(--brand);
}
.view-toolbar {
  position: relative;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 12px;
  border-bottom: 1px solid var(--rule-strong);
  background: white;
}
.view-tabs {
  height: 34px;
  display: flex;
  padding: 2px;
  border: 1px solid var(--rule);
  border-radius: 5px;
  background: var(--sheet-strong);
}
.view-tabs button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  border-radius: 3px;
  background: transparent;
  color: var(--text-soft);
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
}
.view-tabs button.active {
  background: white;
  color: var(--text);
  box-shadow: 0 1px 3px rgba(8, 27, 46, 0.1);
}
.view-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.inline-search {
  height: 34px;
  width: 170px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  border: 1px solid var(--rule);
  border-radius: 5px;
  color: var(--text-faint);
}
.inline-search input {
  min-width: 0;
  width: 100%;
  border: 0;
  background: transparent;
}
.button--filter-active {
  color: var(--brand-strong);
  border-color: var(--brand);
  background: var(--brand-soft);
}
.button--filter-active > span {
  min-width: 16px;
  height: 16px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: white;
  background: var(--brand);
  font-size: 9px;
}
.filter-popover {
  position: absolute;
  z-index: 30;
  top: 46px;
  right: 118px;
  width: 320px;
  display: grid;
  gap: var(--space-3);
  padding: var(--space-4);
  border: 1px solid var(--rule);
  border-radius: 7px;
  background: white;
  box-shadow: var(--shadow-menu);
}
.filter-popover > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.filter-check,
.filter-options label {
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--space-2);
  color: var(--text);
  cursor: pointer;
}
.filter-check input,
.filter-options input {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  accent-color: var(--brand-strong);
}
.filter-check--described {
  align-items: flex-start;
  padding-block: 2px;
}
.filter-check--described input {
  margin-top: 2px;
}
.filter-check--described > span {
  display: grid;
  gap: 1px;
}
.filter-check--described strong {
  font-size: 13px;
}
.filter-check--described small {
  color: var(--text-soft);
  font-size: 11px;
}
.filter-disclosure {
  border-block: 1px solid var(--rule);
}
.filter-disclosure summary {
  min-height: 42px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: var(--space-3);
  color: var(--text);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  list-style: none;
}
.filter-disclosure summary::-webkit-details-marker {
  display: none;
}
.filter-disclosure summary::after {
  content: "";
  width: 7px;
  height: 7px;
  grid-column: 2;
  grid-row: 1;
  margin-right: 2px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(45deg) translateY(-2px);
  transition: transform 120ms var(--ease);
}
.filter-disclosure[open] summary::after {
  transform: rotate(225deg) translate(-2px, -2px);
}
.filter-disclosure summary small {
  grid-column: 1;
  grid-row: 2;
  margin-top: -10px;
  padding-bottom: 8px;
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 400;
}
.filter-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: var(--space-3);
  padding: 0 0 var(--space-3);
}
.filter-options label {
  font-size: 12px;
}
.filter-popover > footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  padding-top: 10px;
  border-top: 1px solid var(--rule);
}
.add-work-layer {
  position: fixed;
  inset: 0;
  z-index: 130;
  display: grid;
  place-items: center;
  padding: var(--space-5);
}
.add-work-scrim {
  position: absolute;
  inset: 0;
  width: 100%;
  background: rgba(8, 27, 46, 0.52);
}
.add-work-dialog {
  position: relative;
  width: min(720px, 100%);
  max-height: calc(100dvh - 40px);
  overflow: hidden;
  border-radius: 10px;
  background: var(--sheet);
  box-shadow: var(--shadow-panel);
}
.add-work-dialog__form {
  max-height: inherit;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
}
.add-work-dialog__header {
  min-height: 82px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  padding: 18px 22px;
  border-bottom: 1px solid var(--rule);
}
.add-work-dialog__header h2 {
  color: var(--ink-900);
}
.add-work-dialog__header p {
  margin-top: var(--space-1);
  color: var(--text-soft);
}
.add-work-dialog__body {
  min-height: 0;
  overflow: auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-content: start;
  gap: var(--space-4);
  padding: 22px;
}
.add-work-dialog__body .field {
  min-width: 0;
}
.internal-only-option {
  min-height: 42px;
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: 8px 10px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  background: var(--sheet-subtle);
  cursor: pointer;
}
.internal-only-option input {
  width: 17px;
  height: 17px;
  margin-top: 1px;
  accent-color: var(--internal);
}
.internal-only-option span {
  display: grid;
  gap: 1px;
}
.internal-only-option small {
  color: var(--text-soft);
  font-size: 11px;
}
.add-work-dialog__name {
  grid-column: 2;
}
.add-work-dialog .input-with-unit {
  position: relative;
}
.add-work-dialog .input-with-unit .input {
  padding-right: 88px;
}
.add-work-dialog .input-with-unit span {
  position: absolute;
  top: 10px;
  right: 9px;
  color: var(--text-soft);
  font-size: 11px;
  pointer-events: none;
}
.add-work-dialog__footer {
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: 13px 22px;
  border-top: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
.add-work-dialog__status {
  min-width: 0;
  color: var(--text-soft);
  font-size: 12px;
}
.add-work-dialog__status .form-error {
  margin: 0;
}
.add-work-dialog__actions {
  display: flex;
  flex: 0 0 auto;
  gap: var(--space-2);
}
.project-canvas {
  min-height: 0;
  overflow: hidden;
}
.list-layout {
  height: 100%;
  min-height: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  overflow: hidden;
}
.list-scroll {
  min-height: 0;
  overflow: auto;
}
.project-loading {
  height: 100%;
  display: grid;
  grid-template-rows: 80px 60px 48px 1fr;
  gap: 1px;
  background: var(--rule);
}
.project-loading .skeleton {
  border-radius: 0;
}
.project-error {
  height: 100%;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 9px;
  padding: 30px;
  text-align: center;
}
.project-error p {
  color: var(--text-soft);
}
.project-error .button {
  margin-top: 6px;
}
.project-header {
  min-width: 0;
  width: 100%;
  overflow: hidden;
}
.project-title-row,
.health-band,
.project-nav,
.view-toolbar,
.project-canvas {
  min-width: 0;
  width: 100%;
}
.project-title h1 {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (max-width: 1100px) {
  .health-band {
    grid-template-columns: 1fr 1fr 1.4fr auto;
  }
  .health-band > :nth-child(4) {
    display: none;
  }
  .inline-search {
    display: none;
  }
  .preset-button {
    display: none;
  }
}
@media (max-width: 760px) {
  .project-title-row {
    padding: 8px 10px;
  }
  .project-title h1 {
    font-size: 17px;
  }
  .bulk-manage-trigger {
    width: 36px;
    min-width: 36px;
    padding-inline: 0;
  }
  .bulk-manage-trigger span {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }
  .health-band {
    grid-template-columns: 1fr 1fr auto;
  }
  .health-band > :nth-child(3) {
    display: none;
  }
  .health-band > div,
  .health-band > .health-summary {
    padding: 0 10px;
  }
  .project-nav {
    padding-inline: 7px;
  }
  .view-toolbar {
    min-width: 0;
    align-items: stretch;
    flex-direction: column;
    padding: 7px 8px;
  }
  .view-tabs {
    min-width: 0;
    width: 100%;
  }
  .view-tabs button {
    min-width: 0;
    flex: 1;
    justify-content: center;
    padding-inline: 4px;
  }
  .view-actions {
    min-width: 0;
    width: 100%;
    justify-content: space-between;
  }
  .view-actions .button--secondary:not(.button--filter-active) {
    padding-inline: 8px;
  }
  .view-actions .button--primary {
    margin-left: 0;
  }
  .filter-popover {
    top: 92px;
    right: 8px;
    left: 8px;
    width: auto;
  }
  .client-logo {
    display: none;
  }
  .bulk-edit-controls {
    grid-template-columns: max-content minmax(0, 1fr) max-content;
    gap: 8px;
    padding: 7px 8px 8px;
  }
  .bulk-selection-count {
    border-right: 0;
    padding-right: 0;
  }
  .bulk-edit-fields {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    overscroll-behavior-inline: contain;
    scrollbar-width: thin;
  }
  .bulk-edit-fields .field {
    flex: 0 0 142px;
    scroll-snap-align: start;
  }
  .bulk-edit-error,
  .bulk-edit-guidance,
  .bulk-schedule-impact {
    margin-inline: 8px;
  }
}
@media (max-width: 600px) {
  .add-work-layer {
    padding: 0;
  }
  .add-work-dialog {
    width: 100%;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
  }
  .add-work-dialog__form {
    height: 100%;
  }
  .add-work-dialog__body {
    grid-template-columns: 1fr;
  }
  .add-work-dialog__name {
    grid-column: auto;
  }
  .add-work-dialog__footer {
    align-items: stretch;
    flex-direction: column;
    gap: var(--space-2);
  }
  .add-work-dialog__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .add-work-dialog__actions .button {
    width: 100%;
  }
}
@media (max-width: 480px) {
  .health-band > :nth-child(2) {
    display: none;
  }
  .health-band {
    grid-template-columns: 1fr auto;
  }
  .view-actions .button--filter-active {
    font-size: 12px;
  }
  .view-actions .button--primary {
    flex: 0 0 auto;
    padding-inline: 9px;
  }
}
</style>
